import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';

// Components
import RequestShippingQuoteModal from 'dibs-request-shipping-modal'; // eslint-disable-line no-unused-vars
import Header from '../Headers/BasicHeader';
import Modals from '../Modals/Modals';
import Recommendations from '../Recommendations/Recommendations';
import SearchResults from '../Headers/SearchResults';
import ItemsRefetchWrapper from './ItemsRefetchWrapper';
import Toolbar from '../Toolbar/Toolbar';

// Helpers
import actions from '../../actions/FavoritesActions';
import classnames from 'classnames';
import get from 'lodash.get';
import {
    copy,
    cutoffs,
    deviceTypes,
    modals,
    mutations,
    tabs,
    sortBy as sortOptions,
} from '../../constants';
import { resetSearchParams } from '../../helpers/urlHelpers';

// Tracking
import trackPageView from '../../helpers/trackPageView';
import trackFavoritesEvent from '../../helpers/trackFavoritesEvent';

// Mutations
import ModifyPortfolioItems from '../../mutations/ModifyPortfolioItems';
import ModifyPortfolios from '../../mutations/ModifyPortfolios';

// Styles
import styles from './styles/ItemsContainer.scss';

const nullResponse = mutations.responses.NULL;
const refetchOptions = { force: true };

export class ItemsContainerComponent extends React.Component {
    constructor(props) {
        super(props);

        this.handleItemUpdate = this.handleItemUpdate.bind(this);
        this.handleMutationResponse = this.handleMutationResponse.bind(this);
        this.itemMutations = this.itemMutations.bind(this);
        this.folderMutations = this.folderMutations.bind(this);
        this.loadItems = this.loadItems.bind(this);
        this.resetUiState = this.resetUiState.bind(this);
    }

    componentWillMount() {
        const { mutationResponse, tabChanged } = this.props;
        this.handleItemUpdate(mutationResponse, tabChanged);
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        trackPageView(this.props.viewMode);
    }

    componentWillReceiveProps(nextProps) {
        const isNextResponseNull =
            this.props.mutationResponse !== nullResponse &&
            nextProps.mutationResponse === nullResponse;
        const isSortByChanged = this.props.sortBy !== nextProps.sortBy;
        const searchChanged = this.props.searchTerm !== nextProps.searchTerm;

        if (isNextResponseNull || isSortByChanged || searchChanged) {
            this.props.setFullPageLoading(true);
            const refetchVariables = fragmentVariables => ({
                ...fragmentVariables,
                sortBy: nextProps.sortBy,
                searchTerm: nextProps.searchTerm,
            });
            this.props.relay.refetch(
                refetchVariables,
                null,
                () => {
                    this.props.setFullPageLoading(false);
                },
                refetchOptions
            );
        }
    }

    componentDidUpdate(prevProps) {
        const { routerNavigation, mutationResponse, isLoadingFullPage, totalResults } = this.props;

        if (prevProps.totalResults < totalResults && mutationResponse === nullResponse) {
            this.props.setOpenModalType(modals.ADD_ITEMS);
            this.props.setShowErrorSuccessModal(true);
            this.props.setFullPageLoading(false);
            this.props.setModalOpen(true);
        }

        // hide page overlay loader when navigating through router
        if (routerNavigation && !prevProps.routerNavigation && isLoadingFullPage) {
            this.props.setFullPageLoading(false);
        }
    }

    handleItemUpdate(response, tabChanged) {
        if (!!tabChanged || response === nullResponse) {
            // resetting total results when switching tabs.
            this.props.resetTotalResults();
            this.props.setFullPageLoading(true);
            // Need to forceFetch after every transition from null-state page
            // because if items tab container is rendering, it means that the user
            // engaged with the null-state page and added an item to their likes
            this.props.relay.refetch(
                fragmentVariables => fragmentVariables,
                null,
                () => {
                    if (response === nullResponse) {
                        this.props.setOpenModalType(modals.ADD_ITEMS);
                        this.props.setShowErrorSuccessModal(true);
                    } else {
                        this.props.setOpenModalType('');
                        this.props.setMutationResponse('');
                    }
                    this.props.setFullPageLoading(false);
                    this.props.setModalOpen(true);
                },
                refetchOptions
            );
        }
    }

    handleMutationResponse(action, response) {
        // Need to forceFetch after every mutation so that post-mutation
        // back-end changes are visually reflected in UI
        this.props.relay.refetch(
            fragmentVariables => fragmentVariables,
            null,
            () => {
                if (action === mutations.types.REORDER) {
                    this.props.setReordered(false);
                } else {
                    this.props.setShowErrorSuccessModal(true);
                    this.props.setModalOpen(true);
                    this.props.setMutationResponse(response);
                }
                this.props.setFullPageLoading(false);
            },
            refetchOptions
        );
    }

    loadItems(first) {
        this.props.setFullPageLoading(true);
        const refetchVariables = fragmentVariables => ({
            ...fragmentVariables,
            first,
        });
        this.props.relay.refetch(refetchVariables, null, () => {
            this.props.setFullPageLoading(false);
            this.props.setNumItems(first);
        });
    }

    itemMutations({
        action,
        targetPortfolioId,
        targetPortfolioItemId,
        startPortfolioItemId,
        endPortfolioItemId,
        note,
    }) {
        this.props.setFullPageLoading(true);
        this.props.setMutationResponse('');
        this.props.setModalOpen(false);
        this.props.setShowErrorSuccessModal(false);
        if (action === mutations.types.REORDER) {
            this.loadItems(cutoffs.ADDITIONAL_ITEMS);
        }
        const portfoliosEdge = get(this.props, 'viewer.itemsPortfolios.edges[0]') || {};
        ModifyPortfolioItems.commit(this.props.relay.environment, {
            action,
            endPortfolioItemId,
            itemsIds: this.props.selectedItems.map(item => item.itemId),
            note,
            portfolioItemsIds: this.props.selectedItems.map(item => item.portfolioItemId),
            portfolioId: targetPortfolioId || get(portfoliosEdge, 'node.serviceId'),
            portfoliosEdge,
            startPortfolioItemId,
            targetPortfolioItemId,
            userId: get(this.props, 'viewer.user.serviceId'),
        })
            .then(() => {
                trackFavoritesEvent({
                    action,
                    setShouldTrackReordering: this.props.setShouldTrackReordering,
                    shouldTrackReordering: this.props.shouldTrackReordering,
                });
                this.handleMutationResponse(action, mutations.responses.SUCCESS);
            })
            .catch(() => {
                this.handleMutationResponse(action, mutations.responses.ERROR);
            });
    }

    folderMutations({ ...args }) {
        const portfoliosEdge = get(this.props, 'viewer.itemsPortfolios.edges[0]') || {};
        return ModifyPortfolios.commit(this.props.relay.environment, {
            ...args,
            portfoliosType: 'HEART',
            portfolioIds: [get(portfoliosEdge, 'node.serviceId')],
            portfoliosEdge,
            userId: get(this.props, 'viewer.user.serviceId'),
        });
    }

    resetUiState() {
        this.props.resetUiState();
        resetSearchParams();
    }

    render() {
        const {
            deviceType,
            favoriteItemOnLoad,
            first,
            hasUserId,
            launchModal,
            openModalType,
            relay,
            searchTerm,
            selectedItemIds,
            selectedItems,
            showDealerBranding,
            showProjects,
            totalResults,
            viewer,
            viewMode,
        } = this.props;
        const userId = get(viewer, 'user.serviceId');
        const portfolioNode = get(viewer, 'itemsPortfolios.edges[0].node');
        const currency = get(viewer, 'user.profile.currency');
        const totalPortfolioItems = get(portfolioNode, 'totalPortfolioItems.totalResults');
        const portfolioId = portfolioNode && portfolioNode.serviceId;
        const stickyClasses = classnames(styles.container, {
            [styles.sticky]: this.props.stickToolbar && deviceType !== deviceTypes.MOBILE,
            [styles.slideIn]: this.props.selecting,
        });
        const selectOptions = [
            { label: copy.sortBy.CUSTOM, value: sortOptions.CUSTOM },
            { label: copy.sortBy.PRICE_ASC, value: sortOptions.PRICE_ASC },
            { label: copy.sortBy.PRICE_DESC, value: sortOptions.PRICE_DESC },
            { label: copy.sortBy.RECENTLY_CREATED, value: sortOptions.RECENTLY_CREATED },
        ];

        if ((totalPortfolioItems === 0 && searchTerm === '') || favoriteItemOnLoad) {
            return (
                <Fragment>
                    <Recommendations
                        favoriteItemOnLoad={favoriteItemOnLoad}
                        hasUserId={hasUserId}
                        portfolioId={portfolioId}
                        selectedItemIds={selectedItemIds}
                        trending={viewer}
                        userId={userId}
                        viewer={viewer}
                    />
                    <Modals
                        folderMutations={(...args) => this.folderMutations(...args)}
                        modalOpen={this.props.modalOpen}
                        mutationResponse={this.props.mutationResponse}
                        openModalType={this.props.openModalType}
                        showErrorSuccessModal={this.props.showErrorSuccessModal}
                    />
                </Fragment>
            );
        } else {
            return (
                <div className={styles.content}>
                    <Header
                        onClick={() => launchModal(modals.SHARE_FOLDER)}
                        onSwitch={(...args) => this.folderMutations(...args)}
                        title="Items"
                        totalItems={totalPortfolioItems}
                    />
                    <div className={stickyClasses}>
                        <Toolbar
                            relay={relay}
                            openModalType={openModalType}
                            removeItems={() => launchModal(modals.REMOVE_ITEMS)}
                            shareItems={() => launchModal(modals.SHARE_ITEMS)}
                            showSearchBar
                            selectedItemIds={selectedItemIds}
                            selectedItems={selectedItems}
                            selectOptions={selectOptions}
                            title="Items"
                            totalItems={totalPortfolioItems}
                            userId={userId}
                            viewer={viewer}
                            viewMode={viewMode}
                        />
                    </div>
                    {this.props.stickToolbar && deviceType !== deviceTypes.MOBILE ? (
                        <div className={styles.shadow} />
                    ) : null}
                    {totalPortfolioItems > cutoffs.MAX_ITEMS ? (
                        <div className={styles.cutoff}>{copy.cuttoffs.ITEMS}</div>
                    ) : null}
                    {searchTerm !== '' && !this.props.isLoadingFullPage && totalResults >= 0 && (
                        <SearchResults
                            onClick={this.resetUiState}
                            searchTerm={searchTerm}
                            totalResults={totalResults}
                        />
                    )}
                    <ItemsRefetchWrapper
                        addNote={() => launchModal(modals.ADD_NOTE)}
                        onDrop={(...args) => this.itemMutations(...args)}
                        first={first}
                        reordered={this.props.reordered}
                        reordering={this.props.reordering}
                        portfolioNode={portfolioNode || null}
                        searchTerm={this.props.searchTerm}
                        showDealerBranding={showDealerBranding}
                        showSeller={showDealerBranding}
                        showProjects={showProjects}
                        sortBy={this.props.sortBy}
                        type={tabs.ITEMS}
                        user={viewer ? viewer.user : null}
                        viewer={viewer || null}
                        viewMode={viewMode}
                        currency={currency}
                        hasUserId={hasUserId}
                        userId={userId}
                        nodeId={portfolioNode.id}
                    />
                    <Modals
                        relay={relay}
                        viewer={viewer}
                        handleMutationResponse={this.handleMutationResponse}
                        selectedItems={this.props.selectedItems}
                        userEmail={get(viewer, 'user.profile.email')}
                        folderMutations={(...args) => this.folderMutations(...args)}
                        itemMutations={this.itemMutations}
                        modalOpen={this.props.modalOpen}
                        mutationResponse={this.props.mutationResponse}
                        openModalType={this.props.openModalType}
                        showErrorSuccessModal={this.props.showErrorSuccessModal}
                    />
                </div>
            );
        }
    }
}

ItemsContainerComponent.propTypes = {
    deviceType: PropTypes.string,
    first: PropTypes.number,
    favoriteItemOnLoad: PropTypes.bool,
    isLoadingFullPage: PropTypes.bool,
    hasUserId: PropTypes.bool,
    launchModal: PropTypes.func,
    modalOpen: PropTypes.bool,
    mutationResponse: PropTypes.string,
    note: PropTypes.string,
    numItems: PropTypes.number,
    openModalType: PropTypes.string,
    relay: PropTypes.object,
    reordered: PropTypes.bool,
    reordering: PropTypes.bool,
    resetEmailState: PropTypes.func,
    resetUiState: PropTypes.func,
    resetTotalResults: PropTypes.func,
    routerNavigation: PropTypes.bool,
    searchTerm: PropTypes.string,
    selectItems: PropTypes.func,
    selectedItemIds: PropTypes.array,
    selectedItems: PropTypes.array,
    selecting: PropTypes.bool,
    setFullPageLoading: PropTypes.func,
    setModalOpen: PropTypes.func,
    setMutationResponse: PropTypes.func,
    setNewPortfolioName: PropTypes.func,
    setPortfolioPrivacy: PropTypes.func,
    setShowErrorSuccessModal: PropTypes.func,
    setNumItems: PropTypes.func,
    setOpenModalType: PropTypes.func,
    setReordered: PropTypes.func,
    setSelectMode: PropTypes.func,
    setShouldTrackReordering: PropTypes.func,
    showErrorSuccessModal: PropTypes.bool,
    showDealerBranding: PropTypes.bool,
    stickToolbar: PropTypes.bool,
    sortBy: PropTypes.string,
    tabChanged: PropTypes.bool,
    shouldTrackReordering: PropTypes.bool,
    showProjects: PropTypes.bool,
    totalResults: PropTypes.number,
    userId: PropTypes.string,
    viewer: PropTypes.object.isRequired,
    viewMode: PropTypes.string,
    page: PropTypes.number,
};

const mapStateToProps = state => {
    return {
        deviceType: state.favorites.uiState.deviceType,
        sortBy: state.favorites.uiState.itemsSort,
        modalOpen: state.favorites.modalState.modalOpen,
        mutationResponse: state.favorites.uiState.mutationResponse,
        openModalType: state.favorites.modalState.openModalType,
        reordering: state.favorites.uiState.reordering,
        searchTerm: state.favorites.uiState.searchTerm,
        page: state.favorites.uiState.page,
        selecting: state.favorites.uiState.selecting,
        stickToolbar: state.favorites.uiState.stickToolbar,
        tabChanged: state.favorites.uiState.tabChanged,
        shouldTrackReordering: state.favorites.uiState.shouldTrackReordering,
        showErrorSuccessModal: state.favorites.modalState.showErrorSuccessModal,
        totalResults: state.favorites.uiState.totalResults,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        resetEmailState: () => {
            dispatch(actions.resetEmailState());
        },
        resetUiState: () => {
            dispatch(actions.resetUiState());
        },
        resetTotalResults: () => {
            dispatch(actions.setTotalResults({ totalResults: -1 }));
        },
        selectItems: selectedItems => {
            dispatch(actions.selectItems({ selectedItems }));
        },
        setFullPageLoading: isLoadingFullPage => {
            dispatch(actions.setFullPageLoading({ isLoadingFullPage }));
        },
        setModalOpen: modalOpen => {
            dispatch(actions.setModalOpen({ modalOpen }));
        },
        setMutationResponse: mutationResponse => {
            dispatch(actions.setMutationResponse({ mutationResponse }));
        },
        setNewPortfolioName: newPortfolioName => {
            dispatch(actions.setNewPortfolioName({ newPortfolioName }));
        },
        setPortfolioPrivacy: newPortfolioPrivacy => {
            dispatch(actions.setPortfolioPrivacy({ newPortfolioPrivacy }));
        },
        setNumItems: first => {
            dispatch(actions.setNumItems({ first }));
        },
        setOpenModalType: openModalType => {
            dispatch(actions.setOpenModalType({ openModalType }));
        },
        setReordered: reordered => {
            dispatch(actions.setReordered({ reordered }));
        },
        setSelectMode: selecting => {
            dispatch(actions.setSelectMode({ selecting }));
        },
        setShouldTrackReordering: shouldTrackReordering => {
            dispatch(actions.setShouldTrackReordering({ shouldTrackReordering }));
        },
        setShowErrorSuccessModal: showErrorSuccessModal => {
            dispatch(actions.setShowErrorSuccessModal({ showErrorSuccessModal }));
        },
    };
};

const ReduxWrapped = connect(
    mapStateToProps,
    mapDispatchToProps
)(ItemsContainerComponent);
const ItemsContainer = createRefetchContainer(
    ReduxWrapped,
    {
        viewer: graphql`
            fragment ItemsContainer_viewer on Viewer {
                ...Recommendations_trending
                ...RequestShippingQuoteModal_viewer
                ...Tiles_viewer
                id
                user(userId: $userId) @include(if: $hasUserId) {
                    serviceId
                    isVerifiedTrade
                    profile {
                        currency
                        email
                    }
                }
                itemsPortfolios: portfolios(
                    first: 1
                    portfolioTypes: $portfolioTypes
                    userIds: $userIds
                ) @include(if: $loadPortfolioData) {
                    edges {
                        node {
                            id
                            isPublic
                            serviceId
                            totalPortfolioItems: items(first: 1) {
                                totalResults
                            }
                            ...ItemsRefetchWrapper_portfolioNode
                        }
                    }
                }
            }
        `,
    },
    graphql`
        query ItemsContainerRefetchQuery(
            $first: Int!
            $after: String
            $hasUserId: Boolean!
            $portfolioTypes: [PortfolioTypes]
            $searchTerm: String
            $loadPortfolioData: Boolean!
            $selectedItemIds: [String]!
            $sortBy: PortfolioItemsSortByTypes
            $userIds: [String]!
            $userId: String!
            $itemId: String!
            $shouldFetchItem: Boolean!
            $showSeller: Boolean!
            $itemIds: [String]
            $shouldFetchConversation: Boolean!
            $shouldFetchTransaction: Boolean!
            $zipCode: String!
            $country: String!
            $canCheckZipCode: Boolean!
            $conversationId: ID!
            $hasConversationId: Boolean!
            $isTrade: Boolean!
            $page: Int!
        ) {
            viewer {
                ...ItemsContainer_viewer
            }
        }
    `
);

export default ItemsContainer;
